import type { AgentHealth } from '@tabel/shared/connector/protocol';
import type { AgentId } from '@tabel/shared/connector/versions';
import {
  AgentCheckResponse,
  ConnectorList,
  type ConnectorView,
  CreateThreadBody,
  DisableResponse,
  EnrollmentIssued,
  FromDayBody,
  FromDayResponse,
  InstallStarted,
  MessageBody,
  MessageResult,
  ModelsResponse,
  PatchThreadBody,
  ResolveBody,
  ResolveResponse,
  RetryQueued,
  StepsResponse,
  StopResponse,
  ThreadDetail,
  ThreadList,
  ThreadResponse,
  type ThreadView,
  TimelineResponse,
} from '@tabel/shared/schemas/agent-client';
import { ApiError } from './errors';
import { contextHeaders } from './role';

/*
 * Адреса браузера 006 (specs/006-agent-client/contracts/http-api.md): компьютеры и агенты, разговоры, лента, сообщения,
 * запросы агента. Запросы — как у остальных страниц (`lib/api.ts`, `pages/MyAgents/api.ts`): сессия
 * (`credentials: 'include'`), роль и объект — заголовками `X-Role` и `X-Object` в каждом запросе. Ошибки — тело 004
 * `{ error: { code, message, requestId, details? } }` → `ApiError` с кодом, текстом и кодом ошибки (requestId); `401
 * UNAUTHENTICATED` — страница входа.
 *
 * Тела и ответы проверяются общими схемами `@tabel/shared/schemas/agent-client` — теми же, что у сервера:
 * - тело не по схеме на сервер не уходит: `ApiError` с кодом `VALIDATION` (статус 0);
 * - ответ не по схеме — `ApiError` с кодом `INTERNAL` и кодом ошибки, а не пустой список.
 * Ответы `check`, `install`, `disable`, `stop`, `resolve`, `retry`, `from-day`, разговор (`ThreadResponse`,
 * `ThreadDetail`) — тоже общими схемами (`AgentCheckResponse`, `InstallStarted`, `DisableResponse`, `StopResponse`,
 * `ResolveResponse`, `RetryQueued`, `FromDayResponse`).
 *
 * Ключ подключения (`issueEnrollment`) приходит только в `command`: модуль его не хранит и не пишет в журнал.
 */

export type { ConnectorView };

/** Роли строки «Подключить и отключить свой компьютер» таблицы прав 006 (`agent.connector`). */
export const CONNECTOR_ROLES = ['rules_admin', 'approver', 'auditor', 'sysadmin'] as const;

/** Ключ кэша списка компьютеров: ответ зависит от роли (право `agent.connector`). */
export const connectorsKey = (role: string) => ['agent-connectors', role] as const;

interface Parser<T> {
  safeParse(v: unknown): { success: true; data: T } | { success: false };
}

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const isText = (v: unknown): v is string => typeof v === 'string';

const badAnswer = (res: Response) =>
  new ApiError(res.status, 'INTERNAL', 'Ошибка сервера', res.headers.get('x-request-id'));

const badBody = () => new ApiError(0, 'VALIDATION', 'Проверьте введённые данные', null);

async function call(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  url: string,
  body?: unknown,
): Promise<{ data: unknown; res: Response }> {
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: 'include',
      headers: {
        ...contextHeaders(),
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, 'NETWORK', 'Нет связи с сервером', null);
  }
  const requestId = res.headers.get('x-request-id');
  if (res.status === 204) return { data: undefined, res };
  const data: unknown = await res.json().catch(() => undefined);
  if (!res.ok) {
    const e = isObject(data) && isObject(data.error) ? data.error : {};
    const code = isText(e.code)
      ? (e.code as ApiError['code'])
      : res.status === 404
        ? 'NOT_FOUND'
        : 'INTERNAL';
    if (res.status === 401 && code === 'UNAUTHENTICATED' && !location.pathname.startsWith('/login'))
      location.assign('/login');
    const details = isObject(e.details) ? e.details : undefined;
    const retryAfter = details?.retryAfterSec;
    throw new ApiError(
      res.status,
      code,
      isText(e.message) ? e.message : `Ошибка ${res.status}`,
      isText(e.requestId) ? e.requestId : requestId,
      typeof retryAfter === 'number' ? retryAfter : undefined,
      details,
    );
  }
  return { data, res };
}

/** Ответ по схеме или `INTERNAL`. */
function answer<T>(schema: Parser<T>, data: unknown, res: Response): T {
  const p = schema.safeParse(data);
  if (!p.success) throw badAnswer(res);
  return p.data;
}

/** Тело по схеме или `VALIDATION` — без запроса. */
function bodyOf<T>(schema: Parser<T>, body: unknown): T {
  const p = schema.safeParse(body);
  if (!p.success) throw badBody();
  return p.data;
}

const enc = encodeURIComponent;
const pc = (id: number) => `/api/agent/connectors/${enc(String(id))}`;
const th = (id: string) => `/api/agent/threads/${enc(id)}`;

/* ---------- Компьютеры и агенты ---------- */

/** Свои компьютеры: в сети или нет, версия коннектора, агенты, число разговоров. */
export async function listConnectors(): Promise<ConnectorView[]> {
  const { data, res } = await call('GET', '/api/agent/connectors');
  return answer(ConnectorList, data, res).connectors;
}

/** Ключ подключения и команда установки (FR-001). Ключ — только в `command`. */
export async function issueEnrollment(): Promise<EnrollmentIssued> {
  const { data, res } = await call('POST', '/api/agent/connectors/enrollments');
  return answer(EnrollmentIssued, data, res);
}

/** Отключить компьютер (FR-004): ключ отозван, коннектор остановится. */
export async function disableConnector(id: number): Promise<void> {
  const { data, res } = await call('POST', `${pc(id)}/disable`);
  answer(DisableResponse, data, res);
}

/** Проверить агента сейчас (RPC `agents.health`). */
export async function checkAgent(id: number, agent: AgentId): Promise<AgentHealth> {
  const { data, res } = await call('POST', `${pc(id)}/agents/${agent}/check`);
  return answer(AgentCheckResponse, data, res).agent;
}

/** Установить или обновить агента командой коннектора (FR-009). `409 INSTALL_RUNNING` — установка уже идёт. */
export async function installAgent(id: number, agent: AgentId, action: 'install' | 'update'): Promise<void> {
  const { data, res } = await call('POST', `${pc(id)}/agents/${agent}/install`, { action });
  answer(InstallStarted, data, res);
}

/** Модели агента: свежесть и запасной список (contracts/ui.md §3). */
export async function agentModels(id: number, agent: AgentId): Promise<ModelsResponse> {
  const { data, res } = await call('GET', `${pc(id)}/agents/${agent}/models`);
  return answer(ModelsResponse, data, res);
}

/** Компьютеры всех пользователей без переписки — администратор системы. */
export async function adminConnectors(): Promise<ConnectorView[]> {
  const { data, res } = await call('GET', '/api/admin/agent-connectors');
  return answer(ConnectorList, data, res).connectors;
}

/* ---------- Разговоры ---------- */

export async function listThreads(archived = false): Promise<ThreadView[]> {
  const { data, res } = await call('GET', `/api/agent/threads?archived=${archived ? 1 : 0}`);
  return answer(ThreadList, data, res).threads;
}

/** Новый разговор: агент должен быть готов, иначе `409 AGENT_NOT_READY`. */
export async function createThread(body: CreateThreadBody): Promise<ThreadView> {
  const { data, res } = await call('POST', '/api/agent/threads', bodyOf(CreateThreadBody, body));
  return answer(ThreadResponse, data, res).thread;
}

/** Разговор и сообщения в очереди («Отправится после ответа»). */
export async function threadDetail(id: string): Promise<ThreadDetail> {
  const { data, res } = await call('GET', th(id));
  return answer(ThreadDetail, data, res);
}

/** Название, модель, уровень рассуждения, архив. */
export async function patchThread(id: string, body: PatchThreadBody): Promise<ThreadView> {
  const { data, res } = await call('PATCH', th(id), bodyOf(PatchThreadBody, body));
  return answer(ThreadResponse, data, res).thread;
}

/** Лента разговора; `afterSequence` — догрузка с номера (US5-1). */
export async function threadTimeline(id: string, afterSequence?: number): Promise<TimelineResponse> {
  const q = afterSequence === undefined ? '' : `?afterSequence=${afterSequence}`;
  const { data, res } = await call('GET', `${th(id)}/timeline${q}`);
  return answer(TimelineResponse, data, res);
}

/** Шаги свёрнутой работы агента. */
export async function turnSteps(id: string, runId: string): Promise<TimelineResponse['rows']> {
  const { data, res } = await call('GET', `${th(id)}/turns/${enc(runId)}/steps`);
  return answer(StepsResponse, data, res).rows;
}

/** Сообщение агенту: `turn`, `steer` («Отправить сразу»), `queue` («Отправить после ответа»). */
export async function sendMessage(id: string, body: MessageBody): Promise<MessageResult> {
  const { data, res } = await call('POST', `${th(id)}/messages`, bodyOf(MessageBody, body));
  return answer(MessageResult, data, res);
}

/** «Убрать» сообщение из очереди. */
export async function dropQueued(id: string, messageKey: string): Promise<void> {
  await call('DELETE', `${th(id)}/queue/${enc(messageKey)}`);
}

/** «Остановить агента»: ответ — когда работа агента закрыта; `409 NOT_RUNNING` — агент не работает. */
export async function stopAgent(id: string): Promise<void> {
  const { data, res } = await call('POST', `${th(id)}/stop`);
  answer(StopResponse, data, res);
}

/** Ответ на запрос агента; `409 INTERACTION_CLOSED` — запрос уже не ждёт (FR-027). */
export async function resolveInteraction(id: string, iid: string, body: ResolveBody): Promise<void> {
  const { data, res } = await call(
    'POST',
    `${th(id)}/interactions/${enc(iid)}/resolve`,
    bodyOf(ResolveBody, body),
  );
  answer(ResolveResponse, data, res);
}

/** «Повторить вопрос» после ошибки. */
export async function retryRun(id: string, runId: string): Promise<void> {
  const { data, res } = await call('POST', `${th(id)}/retry/${enc(runId)}`);
  answer(RetryQueued, data, res);
}

/** «Не повторять» после лимита модели. */
export async function cancelRetry(id: string, runId: string): Promise<void> {
  await call('DELETE', `${th(id)}/retry/${enc(runId)}`);
}

/** «Спросить агента об этом дне» (FR-052): новый разговор и текст для поля сообщения. Сообщение не отправляется. */
export async function threadFromDay(body: FromDayBody): Promise<FromDayResponse> {
  const { data, res } = await call('POST', '/api/agent/threads/from-day', bodyOf(FromDayBody, body));
  return answer(FromDayResponse, data, res);
}

/** Компьютера, разговора или запроса уже нет — или он чужой (`404`, FR-016). */
export const isGone = (err: unknown): boolean => err instanceof ApiError && err.status === 404;
